import { UserEntityPermissionsType } from '@/models/users/user-permissions/user-entity-permissions';
import { Project } from '../projects/project';
import { Pricing } from '../pricings/pricing';
import { BusinessActivity } from './business-activity';
import { BusinessAssociation } from './business-association';
import { BusinessQr } from './business-qr';
import { Link, LinkType } from '@/models/common/link';
import { ConsumerAvailable } from '@/models/common/consumer-available-url-part-info';
import { BusinessType } from './business-type';
import { LinkedBusiness } from './linked-business';

export class Business {
    public id: number = 0;
    public mainProjectId: number = 0;
    public tradeName?: string;
    public ownerId: number = 0;
    public ownerEmail?: string;
    public invitedOwnerEmail?: string;
    public projects: Project[] = [];
    public pricings: Pricing[] = [];
    public type: BusinessType = BusinessType.Business;
    public mainActivity?: BusinessActivity;
    public secondaryActivity?: BusinessActivity;
    public companyId?: number;
    public associations: BusinessAssociation[] = [];
    public topLevelBusinesses: LinkedBusiness[] = [];
    public linkedBusinesses: Business[] = [];
    public selected: boolean = false;
    public isPublished: boolean = false;
    public hasAnSlugDefined: boolean = false;
    public urlsQrPagesRedirectTo: BusinessQr[] = [];
    public pricingLevel: number = 0;
    public externalLinks: Link[] = [];
    public logoUrl: string = '';
    public consumersAvailable: ConsumerAvailable[] = [];

    public get hasFunctionalLevel1(): boolean {
        return this.pricingLevel === 1;
    }

    public get hasFunctionalLevelOver1(): boolean {
        return this.pricingLevel > 1;
    }

    public get hasFunctionalLevel2(): boolean {
        return this.pricingLevel === 2;
    }

    public get hasFunctionalLevelOver2(): boolean {
        return this.pricingLevel > 2;
    }

    public get hasFunctionalLevel2Point5(): boolean {
        return this.pricingLevel === 2.5;
    }

    public get hasFunctionalLevelOver2Point5(): boolean {
        return this.pricingLevel > 2.5;
    }

    public get hasFunctionalLevelUnder3(): boolean {
        return this.pricingLevel < 3;
    }

    public get hasFunctionalLevel3(): boolean {
        return this.pricingLevel === 3;
    }

    public get hasFunctionalLevelOver3(): boolean {
        return this.pricingLevel > 3;
    }

    public get hasFunctionalLevelUnder4(): boolean {
        return this.pricingLevel < 4;
    }

    public get hasFunctionalLevel4(): boolean {
        return this.pricingLevel === 4;
    }

    public get permissionsGroupType(): UserEntityPermissionsType {
        return UserEntityPermissionsType.Businesses;
    }

    public get permissionsIdToCheck(): number {
        return this.id;
    }

    constructor(json?: any) {
        if (!json) {
            return;
        }

        Object.assign(this, json, {
            projects: json.projects ? json.projects.map((project: any) => new Project(project)) : [],
            pricings: json.pricings ? json.pricings.map((pricing: any) => new Pricing(pricing)) : [],
            mainActivity: json.mainActivity ? new BusinessActivity(json.mainActivity) : undefined,
            secondaryActivity: json.secondaryActivity ? new BusinessActivity(json.secondaryActivity) : undefined,
            associations: json.associations ? json.associations.map((association: any) => new BusinessAssociation(association)) : [],
            topLevelBusinesses: json.topLevelBusinesses ? json.topLevelBusinesses.map((topLevelBusinesse: any) => new LinkedBusiness(topLevelBusinesse)) : [],
            linkedBusinesses: json.linkedBusinesses ? json.linkedBusinesses.map((business: any) => new Business(business)): [],
            externalLinks: json.externalLinks ? json.externalLinks.map((link: any) => new Link(link)) : [],
            logoUrl: json.logoUrl ? json.logoUrl : '',
            consumersAvailable: json.consumersAvailable ? json.consumersAvailable.map((ca: any) => new ConsumerAvailable(ca)) : []
        });

        this.orderExternalLinks();
    }

    get value(): number {
        return this.id;
    }

    get text(): string | undefined {
        return this.tradeName;
    }

    get associationNames(): string | undefined {
        return this.associations && this.associations.length > 0 ? this.associations.map(a => a.associationName).join(', ') : '';
    }

    get topLevelBusinessTradeNames(): string | undefined {
        return this.topLevelBusinesses && this.topLevelBusinesses.length > 0 ? this.topLevelBusinesses.map(tb => tb.topLevelBusinessTradeName).join(', ') : '';
    }

    public clone(): Business {
        const business: Business = new Business();

        business.id = this.id;
        business.tradeName = this.tradeName;
        business.ownerId = this.ownerId;
        business.ownerEmail = this.ownerEmail;
        business.invitedOwnerEmail = this.invitedOwnerEmail;
        business.projects.push(...this.projects.map(p => p.clone()));
        business.pricings.push(...this.pricings.map(p => p.clone()));
        business.type = this.type;
        business.mainActivity = this.mainActivity ? this.mainActivity.clone() : undefined;
        business.secondaryActivity = this.secondaryActivity ? this.secondaryActivity.clone() : undefined;
        business.companyId = this.companyId;
        business.associations.push(...this.associations.map(a => new BusinessAssociation(a).clone()));
        business.topLevelBusinesses.push(...this.topLevelBusinesses.map(tb => new LinkedBusiness(tb).clone()));
        business.isPublished = this.isPublished;
        business.hasAnSlugDefined = this.hasAnSlugDefined;
        business.urlsQrPagesRedirectTo.push(...this.urlsQrPagesRedirectTo.map(url => new BusinessQr(url).clone()));
        business.externalLinks.push(...this.externalLinks.map(sml => new Link(sml).clone()));
        business.pricingLevel = this.pricingLevel;
        business.logoUrl = this.logoUrl;

        return business;
    }

    private orderExternalLinks() {
        if (this.externalLinks && this.externalLinks.length > 0) {
            const whatsAppIndex = this.externalLinks.findIndex((element: Link) => element.type === LinkType.WhatsApp);
            const whatsAppLink = this.externalLinks.splice(whatsAppIndex, 1)[0];
            this.externalLinks.splice(0, 0, whatsAppLink);

            const myWebSiteIndex = this.externalLinks.findIndex((element: Link) => element.type === LinkType.MyWebSite);
            const myWebSiteLink = this.externalLinks.splice(myWebSiteIndex, 1)[0];
            this.externalLinks.splice(10, 0, myWebSiteLink);

            const myOnlineShopIndex = this.externalLinks.findIndex((element: Link) => element.type === LinkType.MyOnlineShop);
            const myOnlineShopLink = this.externalLinks.splice(myOnlineShopIndex, 1)[0];
            this.externalLinks.splice(10, 0, myOnlineShopLink);
        }
    }
}
